import { Users } from 'lucide-react'
import { useI18n } from '../lib/i18n'
import { formatScope, parseScope, type TeamFilter } from '../lib/teamFilter'
import type { Team } from '../lib/types'
import { TeamChips } from './TeamChips'

/** Teams in order, each followed by its squads; squads on their own at the end. The server sends them so. */
function label(team: Team, teams: Team[]): string {
  if (team.kind !== 'Squad') return team.name
  return team.parentTeamId && teams.some((t) => t.id === team.parentTeamId) ? `\u00A0\u00A0↳ ${team.name}` : `◦ ${team.name}`
}

/**
 * Whom a manager is looking at: everybody, a team, a squad, or one developer. With a developer
 * chosen, the teams and squads they are in show beside it. Draws nothing unless there is a choice.
 */
export function TeamPicker({ filter, className }: { filter: TeamFilter; className?: string }) {
  const { t } = useI18n()
  if (!filter.show) return null

  return (
    <span className={`team-picker-wrap${className ? ` ${className}` : ''}`}>
      <label className="team-picker" title={t('teamPicker.label')}>
        <Users size={13} aria-hidden />
        <select
          className="team-picker__select"
          value={formatScope(filter.scope)}
          onChange={(event) => filter.setScope(parseScope(event.target.value))}
          aria-label={t('teamPicker.label')}
        >
          <option value="">{t('teamPicker.all')}</option>
          {filter.teams.length > 0 && (
            <optgroup label={t('teamPicker.teams')}>
              {filter.teams.map((team) => (
                <option key={team.id} value={formatScope({ type: 'team', id: team.id })}>
                  {label(team, filter.teams)}
                </option>
              ))}
            </optgroup>
          )}
          {filter.developers.length > 0 && (
            <optgroup label={t('teamPicker.developers')}>
              {filter.developers.map((developer) => (
                <option key={developer.id} value={formatScope({ type: 'developer', id: developer.id })}>
                  {developer.displayName}
                </option>
              ))}
            </optgroup>
          )}
        </select>
      </label>

      {filter.developerId && (
        <TeamChips
          teams={filter.developerTeams}
          empty={t('teamPicker.noTeam')}
          onPick={(teamId) => filter.setScope({ type: 'team', id: teamId })}
        />
      )}
    </span>
  )
}
